import { Controller } from "@hotwired/stimulus"
import { TtsClient, processMessage, sanitizeForTts, stripTags } from "@primeta/persona-core"

// Shared AudioContext — must be created/resumed during a user gesture for mobile
let sharedAudioCtx = null
// Dedicated silent <audio> element kept around and re-played on each warm call
// so the iOS audio session stays in "playback" mode across gestures. A fresh
// Audio() per call wouldn't inherit the unlock — reusing one does.
let silentPrimerAudio = null
const SILENT_MP3_DATA_URL = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAAbAAqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dX/////////////////////////////AAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA/+M4xAANaALkAUAAAP/jOMQJDSgCgBQBAAAAAAAAAAAAAAAAAAA='

function warmAudioContext() {
  if (!sharedAudioCtx) {
    sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)()
  }
  if (sharedAudioCtx.state === 'suspended' || sharedAudioCtx.state === 'interrupted') {
    sharedAudioCtx.resume()
  }

  // iOS silent mode bypass: play a short silent buffer through the AudioContext
  // during a user gesture. Runs every call because iOS can re-lock the audio
  // session between gestures — priming again on each warm keeps it in
  // "playback" mode.
  if (sharedAudioCtx.state === 'running') {
    try {
      const silentBuffer = sharedAudioCtx.createBuffer(1, sharedAudioCtx.sampleRate * 0.25, sharedAudioCtx.sampleRate)
      const src = sharedAudioCtx.createBufferSource()
      src.buffer = silentBuffer
      src.connect(sharedAudioCtx.destination)
      src.start()
    } catch {}
  }

  // Reuse a single HTMLAudio element so iOS keeps the unlock across plays.
  try {
    if (!silentPrimerAudio) {
      silentPrimerAudio = new Audio(SILENT_MP3_DATA_URL)
      silentPrimerAudio.loop = false
    }
    silentPrimerAudio.currentTime = 0
    silentPrimerAudio.play().catch(() => {})
  } catch {}

  return sharedAudioCtx
}

function csrfToken() {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta ? meta.content : ''
}

export default class extends Controller {
  static targets = ["input", "autoplayBtn", "autoplayIcon", "autoplayLabel", "stopBtn", "emotionOverlay", "chatPanel", "chatToggle", "bgToggle", "bgIcon", "volumeSlider", "avatarThinking"]
  static values = {
    conversationId: Number,
    voiceId: { type: String, default: "" },
    autoplay: { type: Boolean, default: true },
    stageAdvanceUrl: { type: String, default: "" }
  }

  connect() {
    this.sentenceBuffer = ''
    this.isPlaying = false
    this.ttsEnabled = false
    this.innerObserver = null
    this.lastObservedLength = 0

    this._ttsClient = new TtsClient({
      playAnimation: (name) => this.triggerBodyAnimation(name),
      onSpeechDone: () => this._onSpeechDone(),
      getVrm: () => document.querySelector('[data-controller~="avatar"]')?.avatarController?._avatar?.vrm,
      getAvatar: () => document.querySelector('[data-controller~="avatar"]')?.avatarController?._avatar,
    })
    this._ttsClient.voiceId = this.voiceIdValue || null
    this._ttsClient.muted = !this.autoplayValue

    this.setupStreamingObserver()
    this.setupPersonaObserver()
    this.updateAutoplayUI()

    // Every click/key/touch on the page warms the audio context. Staying
    // registered (not auto-removing) means each gesture re-primes the iOS
    // audio session, so the session can't get silently re-locked between
    // actions.
    this._warmOnGesture = () => { warmAudioContext() }
    document.addEventListener('click', this._warmOnGesture, { capture: true })
    document.addEventListener('keydown', this._warmOnGesture, { capture: true })
    document.addEventListener('touchstart', this._warmOnGesture, { capture: true })

    // Autoplay denied mid-speech: drop the remaining queue so we don't
    // hammer the same NotAllowedError, but leave autoplay ON. The next
    // user gesture (any click) re-warms the audio session via the
    // capture-phase listener, and the next reply can try again.
    this._onTtsBlocked = () => {
      this.cancelSpeech()
    }
    window.addEventListener('tts:blocked', this._onTtsBlocked)
  }

  disconnect() {
    this.cancelSpeech()
    this.outerObserver?.disconnect()
    this.innerObserver?.disconnect()
    this.personaObserver?.disconnect()
    if (this._onTtsBlocked) window.removeEventListener('tts:blocked', this._onTtsBlocked)
    if (this._warmOnGesture) {
      document.removeEventListener('click', this._warmOnGesture, { capture: true })
      document.removeEventListener('keydown', this._warmOnGesture, { capture: true })
      document.removeEventListener('touchstart', this._warmOnGesture, { capture: true })
    }
  }

  send(event) {
    event.preventDefault()
    this.sendMessage(this.inputTarget.value.trim())
  }

  _showAvatarThinking(show) {
    if (!this.hasAvatarThinkingTarget) return
    this.avatarThinkingTarget.classList.toggle("hidden", !show)
  }

  receiveTranscript(event) {
    const { text, autoSend } = event.detail
    if (!text?.trim()) return
    if (this.autoplayValue) warmAudioContext()
    if (autoSend) {
      this.sendMessage(text.trim())
    } else {
      this.inputTarget.value = text.trim()
      this._autoresizeInput()
      this.inputTarget.focus()
    }
  }

  handleInput() {
    this._autoresizeInput()
    const hasText = this.inputTarget.value.trim().length > 0
    if (hasText && !this._isListening) {
      this._isListening = true
      this.triggerBodyAnimation('idle')
    } else if (!hasText && this._isListening) {
      this._isListening = false
      this.triggerBodyAnimation('idle')
    }
  }

  // Enter sends the message; Shift+Enter inserts a newline so long voice
  // transcripts can be edited before sending.
  handleInputKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey && !event.isComposing) {
      event.preventDefault()
      event.currentTarget.form?.requestSubmit()
    }
  }

  // Textareas don't grow on their own. Set height to scrollHeight so the
  // composer expands with content, capped by the CSS max-h. Fallback for
  // browsers that don't support `field-sizing: content`.
  _autoresizeInput() {
    if (!this.hasInputTarget) return
    const el = this.inputTarget
    if (el.tagName !== "TEXTAREA") return
    el.style.height = "auto"
    el.style.height = `${el.scrollHeight}px`
  }

  sendMessage(content) {
    if (!content) return
    if (this.autoplayValue) warmAudioContext()

    this.inputTarget.value = ""
    this._autoresizeInput()
    this._isListening = false
    this.cancelSpeech()
    this.lastObservedLength = 0
    this.sentenceBuffer = ''
    this.triggerBodyAnimation('thinking')
    this._showAvatarThinking(true)

    // Optimistically append user message
    const messagesEl = document.getElementById('messages')
    if (messagesEl) {
      const now = new Date().toISOString()
      const escaped = content.replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      const msgHtml = `<div class="flex justify-end" data-optimistic="true">
        <div class="group relative bg-accent/15 border border-accent/30 text-fg-on-dark max-w-2xl rounded-lg px-4 py-2.5 text-sm leading-relaxed">
          <span>${escaped}</span>
          <time class="block text-[10px] text-fg-muted-on-dark/80 mt-1 text-right" style="font-family: var(--font-mono);"
                data-controller="local-time" datetime="${now}"></time>
        </div>
      </div>`
      messagesEl.insertAdjacentHTML('beforeend', msgHtml)
      requestAnimationFrame(() => { messagesEl.scrollTop = messagesEl.scrollHeight })
    }

    fetch(`/conversations/${this.conversationIdValue}/messages`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": csrfToken()
      },
      body: new URLSearchParams({ content })
    }).catch(() => {
      this.triggerBodyAnimation('idle')
    })
  }

  // --- TTS token management ---


  _onSpeechDone() {
    this._ttsQueueBusy = false
    // If there's another queued sentence, speak it immediately without
    // flipping to idle — keeps the avatar in talking mode seamlessly.
    if (this._ttsQueue && this._ttsQueue.length > 0) {
      this._drainTtsQueue()
      return
    }
    this.isPlaying = false
    this.updateStopBtn()
    this.triggerBodyAnimation('idle')
    document.dispatchEvent(new CustomEvent("tts:end"))

    // Stage mode: tell the server this side has finished speaking so the
    // paired conversation can take its turn. Without this signal the relay
    // would never advance — it's now strictly speech-driven, no timer.
    if (this.stageAdvanceUrlValue) {
      fetch(this.stageAdvanceUrlValue, {
        method: "POST",
        headers: { "X-CSRF-Token": csrfToken(), "Accept": "application/json" },
        credentials: "same-origin"
      }).catch((err) => console.warn("[stage] advance failed:", err))
    }
  }

  // --- Autoplay toggle ---

  cycleBackground() {
    if (!this.hasBgToggleTarget) return
    const btn = this.bgToggleTarget
    const hasImage = btn.dataset.bgUrl && btn.dataset.bgUrl.length > 0
    const modes = hasImage ? ["default", "light", "dark", "image"] : ["default", "light", "dark"]
    const current = btn.dataset.bgMode || "default"
    const next = modes[(modes.indexOf(current) + 1) % modes.length]
    btn.dataset.bgMode = next

    const avatarWrapper = document.querySelector('[data-controller="avatar"]')
    if (!avatarWrapper) return

    // Reset
    avatarWrapper.style.backgroundImage = ''
    avatarWrapper.style.backgroundSize = ''
    avatarWrapper.style.backgroundPosition = ''
    avatarWrapper.style.backgroundColor = ''

    switch (next) {
      case "light":
        avatarWrapper.style.backgroundColor = '#f5f3f0'
        btn.classList.add('text-accent')
        btn.classList.remove('text-white/80')
        break
      case "dark":
        avatarWrapper.style.backgroundColor = '#1a1a18'
        btn.classList.add('text-accent')
        btn.classList.remove('text-white/80')
        break
      case "image":
        avatarWrapper.style.backgroundImage = `url(${btn.dataset.bgUrl})`
        avatarWrapper.style.backgroundSize = 'cover'
        avatarWrapper.style.backgroundPosition = 'center'
        btn.classList.add('text-accent')
        btn.classList.remove('text-white/80')
        break
      default:
        btn.classList.remove('text-accent')
        btn.classList.add('text-white/80')
    }
  }

  toggleChat() {
    if (!this.hasChatPanelTarget) return
    this.chatPanelTarget.classList.toggle("hidden")
    // Scroll messages to bottom when opening
    if (!this.chatPanelTarget.classList.contains("hidden")) {
      const messages = this.chatPanelTarget.querySelector('#messages')
      if (messages) {
        requestAnimationFrame(() => { messages.scrollTop = messages.scrollHeight })
      }
    }
  }

  toggleAutoplay() {
    this.autoplayValue = !this.autoplayValue
    this._ttsClient.muted = !this.autoplayValue
    // Audio context + gain node setup is handled lazily in _drainTtsQueue
    // — the capture-phase document listener primes the iOS session for us.
    // Don't touch the audio graph here.
    this.updateAutoplayUI()
    if (!this.autoplayValue) this.cancelSpeech()
  }

  setVolume() {
    if (!this.hasVolumeSliderTarget) return
    const vol = parseInt(this.volumeSliderTarget.value, 10) / 100
    if (this._ttsClient) {
      this._ttsClient._volume = vol
      if (this._ttsClient._gainNode) {
        this._ttsClient._gainNode.gain.value = vol
      }
      const wasMuted = this._ttsClient.muted
      this._ttsClient.muted = vol === 0
      this.autoplayValue = vol > 0
      if (vol === 0 && !wasMuted) this.cancelSpeech()
    }
  }

  // Dismiss the audio unlock overlay. Actual audio context warming happens
  // on the same click via the capture-phase document listener.
  dismissAudioUnlock(event) {
    this.autoplayValue = true
    this._ttsClient.muted = false
    this.updateAutoplayUI()
    const overlay = event?.currentTarget || document.getElementById("audio-unlock-overlay")
    if (!overlay) return
    overlay.style.opacity = "0"
    overlay.style.pointerEvents = "none"
    setTimeout(() => overlay.remove(), 300)
  }

  updateAutoplayUI() {
    if (this.hasAutoplayLabelTarget) {
      this.autoplayLabelTarget.textContent = this.autoplayValue ? "Voice on" : "Voice off"
    }
    if (this.hasAutoplayBtnTarget) {
      this.autoplayBtnTarget.classList.toggle("text-accent", this.autoplayValue)
      this.autoplayBtnTarget.classList.toggle("text-fg-muted", !this.autoplayValue)
      this.autoplayBtnTarget.title = this.autoplayValue ? "Voice on" : "Voice off (tap to enable)"
      // Drive the pulsing glow while voice is off so users know where to tap.
      if (this.autoplayValue) {
        this.autoplayBtnTarget.removeAttribute("data-voice-off")
      } else {
        this.autoplayBtnTarget.setAttribute("data-voice-off", "true")
      }
    }
    if (this.hasAutoplayIconTarget) {
      // Switch between speaker and muted speaker icons
      const on = this.autoplayValue
      this.autoplayIconTarget.innerHTML = on
        ? '<path d="M10 3.75a.75.75 0 00-1.264-.546L4.703 7H3.167a.75.75 0 00-.7.48A6.985 6.985 0 002 10c0 .887.165 1.737.468 2.52.111.29.39.48.699.48h1.536l4.033 3.796A.75.75 0 0010 16.25V3.75zM15.95 5.05a.75.75 0 00-1.06 1.06 5.5 5.5 0 010 7.78.75.75 0 001.06 1.06 7 7 0 000-9.9z" /><path d="M13.829 7.172a.75.75 0 00-1.06 1.06 2.5 2.5 0 010 3.536.75.75 0 001.06 1.06 4 4 0 000-5.656z" />'
        : '<path d="M9.547 3.062A.75.75 0 0110 3.75v12.5a.75.75 0 01-1.264.546L4.703 13H3.167a.75.75 0 01-.7-.48A6.985 6.985 0 012 10c0-.887.165-1.737.468-2.52a.75.75 0 01.699-.48h1.536l4.033-3.796a.75.75 0 01.811-.142zM13.28 7.22a.75.75 0 10-1.06 1.06L13.94 10l-1.72 1.72a.75.75 0 101.06 1.06L15 11.06l1.72 1.72a.75.75 0 101.06-1.06L16.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L15 8.94l-1.72-1.72z" />'
    }
  }

  // --- Stop speech ---

  stopSpeech() {
    this.cancelSpeech()
    this.triggerBodyAnimation('idle')
    this.updateStopBtn()
  }

  updateStopBtn() {
    if (!this.hasStopBtnTarget) return
    this.stopBtnTarget.classList.toggle('hidden', !this.isPlaying)
  }

  // --- Emotion overlay ---

  showEmotion(emotion) {
    if (!this.hasEmotionOverlayTarget) return
    const el = this.emotionOverlayTarget
    el.textContent = emotion
    el.style.opacity = '1'
    clearTimeout(this._emotionTimer)
    this._emotionTimer = setTimeout(() => {
      el.style.opacity = '0'
    }, 2500)
  }

  // --- Replay ---

  replay(event) {
    const text = event.currentTarget.dataset.text
    if (!text) return

    warmAudioContext()
    this.cancelSpeech()
    this._replayText(text, event.currentTarget.dataset.messageId)
  }

  replayLast() {
    if (!this._lastSpokenText) return
    warmAudioContext()
    this.cancelSpeech()
    this._replayText(this._lastSpokenText)
  }

  // Split into sentences and enqueue each one so first-word latency matches
  // the streaming path.
  _replayText(text, messageId = null) {
    this._lastSpokenText = text
    this.isPlaying = true
    this.updateStopBtn()
    document.dispatchEvent(new CustomEvent("tts:start"))

    // Replay is a user action — always speak regardless of ttsEnabled state
    const rawSentences = this._splitSentences(text)
    rawSentences.forEach(rawChunk => {
      const { emotion: sEmotion, intensity: sIntensity, ttsText: sText } = processMessage(rawChunk, { allowedTags: this._availableEmotions })
      if (sText && sText.length >= 3) {
        this._enqueueSentence(sText, sEmotion, sIntensity, messageId)
      }
    })
  }

  // --- Streaming observation ---

  setupStreamingObserver() {
    const messagesEl = document.getElementById('messages')
    if (!messagesEl) return

    this.outerObserver = new MutationObserver((mutations) => {
      const streamingEl = document.getElementById('assistant-streaming')

      if (streamingEl && !this.innerObserver) {
        this.ttsEnabled = streamingEl.dataset.ttsEnabled === 'true'
        this.triggerBodyAnimation('talking')
        this.attachInnerObserver()
      }

      if (!streamingEl && this.innerObserver) {
        this.flushAndCleanup()
        this._showAvatarThinking(false)
        if (!this.isPlaying && (this._ttsQueue?.length ?? 0) === 0) {
          this.triggerBodyAnimation('idle')
        }
      }

      // Bridge unprompted messages — full text arrives in one shot, so split
      // into sentences and queue them individually for faster first-word.
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue

          // User message arriving via Turbo Stream (sender is external — e.g.
          // OpenClaw terminal, Claude Code, etc.). Trigger the "thinking" UI
          // so the desktop/web overlay reflects that a reply is in flight.
          // Skip our own optimistic insert (already handled by sendMessage).
          const userBubble = node.classList?.contains('justify-end')
            ? node
            : node.querySelector?.('.flex.justify-end[data-message-id]')
          if (userBubble && !node.dataset?.optimistic && !userBubble.dataset?.optimistic) {
            this.triggerBodyAnimation('thinking')
            this._showAvatarThinking(true)
          }

          const pending = node.dataset?.ttsPending ? node : node.querySelector?.('[data-tts-pending]')
          if (pending && pending.dataset.ttsPending) {
            delete pending.dataset.ttsPending
            this._showAvatarThinking(false)
            // Dedupe by message-id in case the same node gets re-observed
            // (e.g. when turbo batches replace + other mutations in one
            // task and the observer fires twice).
            const msgId = pending.dataset.messageId || pending.id
            if (msgId) {
              this._spokenMessageIds = this._spokenMessageIds || new Set()
              if (this._spokenMessageIds.has(msgId)) continue
              this._spokenMessageIds.add(msgId)
            }
            // Prefer the raw content with emotion tags intact (data-raw) over
            // the visually-stripped .msg-text textContent. processMessage
            // needs the [happy]/[angry]/etc. brackets to extract emotions.
            const raw = pending.dataset.raw || pending.querySelector('.msg-text')?.textContent || ''
            const { emotion, intensity, ttsText } = processMessage(raw, { allowedTags: this._availableEmotions })
            const wasIdle = !this.isPlaying
            if (emotion && wasIdle) {
              this.triggerFacialEmotion(emotion, intensity)
              this.showEmotion(emotion)
            }
            if (ttsText) {
              warmAudioContext()
              if (wasIdle) {
                this._lastSpokenText = raw
                this.isPlaying = true
                this.updateStopBtn()
                document.dispatchEvent(new CustomEvent("tts:start"))
              }

              // Split the RAW text (with emotion tags) so each sentence can
              // trigger its own facial expression as it plays.
              const numericMsgId = pending.dataset.messageId || null
              const rawSentences = this._splitSentences(raw)
              rawSentences.forEach(rawChunk => {
                const { emotion: sEmotion, intensity: sIntensity, ttsText: sText } = processMessage(rawChunk, { allowedTags: this._availableEmotions })
                if (sText && sText.length >= 3) {
                  this._enqueueSentence(sText, sEmotion, sIntensity, numericMsgId)
                }
              })
            }
          }
        }
      }
    })

    this.outerObserver.observe(messagesEl, { childList: true })
  }

  _readAvailableEmotions(el) {
    const raw = el?.dataset?.availableEmotions || ''
    const list = raw.split(',').map(s => s.trim().toLowerCase()).filter(Boolean)
    // Null means "allow everything" (sanitizeForTts's legacy path). Use that
    // as a fallback when the server didn't render the attribute at all so we
    // don't silently filter every tag to nothing.
    return list.length ? new Set(list) : null
  }

  setupPersonaObserver() {
    const stateEl = document.getElementById('persona-state')
    if (!stateEl) return

    this._lastPersonaSlug = stateEl.dataset.personaSlug
    this._availableEmotions = this._readAvailableEmotions(stateEl)

    this.personaObserver = new MutationObserver(() => {
      const el = document.getElementById('persona-state')
      if (!el) return
      const slug = el.dataset.personaSlug
      const id = el.dataset.personaId
      if (!slug || slug === this._lastPersonaSlug) return
      this._lastPersonaSlug = slug
      this._availableEmotions = this._readAvailableEmotions(el)

      // Update avatar controller
      const avatarEl = document.querySelector('[data-controller="avatar"]')
      if (avatarEl) {
        avatarEl.dataset.avatarPersonaValue = slug
        avatarEl.dataset.avatarPersonaIdValue = id
      }

      // Update theme controller
      const themeEl = document.querySelector('[data-controller="theme"]')
      if (themeEl) {
        themeEl.dataset.themePersonaValue = slug
        themeEl.dataset.themePersonaIdValue = id
      }

      // Update voice for new persona
      const voiceId = el.dataset.voiceId
      if (voiceId) {
        this.voiceIdValue = voiceId
        this._ttsClient.voiceId = voiceId
      }

      // Update background URL for new persona, reset to default mode
      if (this.hasBgToggleTarget) {
        this.bgToggleTarget.dataset.bgUrl = el.dataset.bgUrl || ''
        this.bgToggleTarget.dataset.bgMode = 'default'
        this.bgToggleTarget.classList.remove('text-accent')
        this.bgToggleTarget.classList.add('text-white/80')
      }
      if (avatarEl) {
        avatarEl.style.backgroundImage = ''
        avatarEl.style.backgroundSize = ''
        avatarEl.style.backgroundPosition = ''
        avatarEl.style.backgroundColor = ''
      }
    })

    this.personaObserver.observe(stateEl.parentElement, { childList: true, subtree: true })
  }

  attachInnerObserver() {
    const streamingContent = document.getElementById('streaming-content')
    if (!streamingContent) return

    this.streamingContentEl = streamingContent
    this.fullStreamedText = ''
    this._spokenLength = 0  // characters already handed to TTS

    this.innerObserver = new MutationObserver(() => {
      const thinkingEl = document.getElementById('thinking-indicator')
      if (thinkingEl) thinkingEl.remove()
      this._showAvatarThinking(false)
      // The outer observer handles TTS enqueue when the finalized message
      // partial lands (driven by its data-tts-pending flag). Inner just
      // mirrors the streamed text for any UI that reads from it.
      this.fullStreamedText = streamingContent.textContent || ''
    })

    this.innerObserver.observe(streamingContent, {
      childList: true,
      characterData: true,
      subtree: true
    })
  }

  // As the LLM streams, peel off complete sentences and queue them for TTS
  // individually. This lets the avatar start talking within ~1s of the first
  // sentence completing, rather than waiting for the full message.
  _maybeSpeakNextSentence() {
    if (!this.ttsEnabled) return
    // Drain every complete sentence available — a single-frame bridge
    // reply delivers the full text in one observer fire, so looping here
    // means we queue all of it instead of just the first sentence.
    while (true) {
      const unspoken = this.fullStreamedText.slice(this._spokenLength)
      const match = unspoken.match(/^[\s\S]*?[.!?\n](?=\s|$)/)
      if (!match) break
      const consumedLen = match[0].length
      const chunk = match[0].trim()
      this._spokenLength += consumedLen
      if (chunk.length < 3) continue

      const { emotion, intensity, ttsText } = processMessage(chunk, { allowedTags: this._availableEmotions })
      if (emotion) {
        this.triggerFacialEmotion(emotion, intensity)
        this.showEmotion(emotion)
      }
      if (ttsText) {
        warmAudioContext()
        this._enqueueSentence(ttsText)
      }
    }
  }

  // Sentence-level TTS queue. TtsClient.speak() cancels any in-flight speech
  // when called, so we can't fire multiple speaks at once. Instead we queue
  // and wait for onSpeechDone to drain.
  // Split text into sentences, respecting common abbreviations like Mr., Dr.
  // Also handles inline emotion tags by keeping them with their sentence.
  _splitSentences(text) {
    const ABBREV = /\b(Mr|Mrs|Ms|Dr|Sr|Jr|St|vs|etc|e\.g|i\.e|Prof)\.$/i
    const out = []
    let buf = ''
    // Walk char by char tracking sentence boundaries
    for (let i = 0; i < text.length; i++) {
      const c = text[i]
      buf += c
      if (c === '.' || c === '!' || c === '?' || c === '\n') {
        const next = text[i + 1] || ' '
        // End-of-sentence: punctuation followed by whitespace or end-of-text
        if (/\s/.test(next) || i === text.length - 1) {
          // Don't split on abbreviations
          if (ABBREV.test(buf.trim())) continue
          out.push(buf.trim())
          buf = ''
        }
      }
    }
    if (buf.trim().length > 0) out.push(buf.trim())
    return out
  }

  _enqueueSentence(text, emotion = null, intensity = 1.0, messageId = null) {
    this._ttsQueue = this._ttsQueue || []
    const item = { text, emotion, intensity, promise: this._prefetchTts(text, messageId) }
    const wasIdle = !this.isPlaying
    this._ttsQueue.push(item)
    // Streaming path (new bridge replies) queues sentences here without
    // pre-flipping isPlaying. Flip it now so the Stop button appears.
    if (wasIdle) {
      this.isPlaying = true
      this.updateStopBtn()
      document.dispatchEvent(new CustomEvent("tts:start"))
    }
    this._drainTtsQueue()
  }

  // Fire off the /tts_proxy JSON request immediately so sentence N+1 is
  // synthesizing in parallel with sentence N's playback. Returns a promise
  // that resolves to the JSON response.
  _prefetchTts(text, messageId = null) {
    const voiceId = this._ttsClient?.voiceId
    if (!voiceId) return Promise.resolve(null)
    const csrf = document.querySelector('meta[name="csrf-token"]')?.content
    const body = { text, voice_id: voiceId, format: 'json' }
    if (messageId) body.message_id = messageId
    return fetch('/tts_proxy', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(csrf ? { 'X-CSRF-Token': csrf } : {})
      },
      body: JSON.stringify(body)
    }).then(async r => {
      if (!r.ok) return null
      const data = await r.json()
      if (data?.error === 'trial_credits_exhausted') {
        this._showTrialCreditsExhausted()
        return null
      }
      return data
    }).catch(() => null)
  }

  _showTrialCreditsExhausted() {
    if (this._trialBannerShown) return
    this._trialBannerShown = true
    const banner = document.createElement('div')
    banner.id = 'trial-credits-banner'
    banner.className = 'fixed top-4 left-1/2 -translate-x-1/2 z-50 max-w-lg w-full px-4'
    banner.innerHTML = `
      <div class="bg-white border border-accent/30 shadow-lg p-4" style="font-family: var(--font-sans);">
        <p class="text-sm font-semibold text-fg mb-1">Voice credits used up</p>
        <p class="text-xs text-fg-muted mb-3">Your trial voice minutes are finished. Start your plan to get full voice access.</p>
        <div class="flex gap-2">
          <a href="/settings?tab=account" class="btn-primary px-3 py-1.5 text-xs" style="text-decoration:none;">Start Plan Now</a>
          <button onclick="this.closest('#trial-credits-banner').remove()" class="btn-outline px-3 py-1.5 text-xs cursor-pointer">Dismiss</button>
        </div>
      </div>
    `
    document.body.appendChild(banner)
  }

  async _drainTtsQueue() {
    if (this._ttsQueueBusy) return
    // Respect the mute toggle. _ttsClient.speak() checks this too, but the
    // VRM path below bypasses speak() and goes straight to _playWithLipSync,
    // so we need the guard here as well.
    if (this._ttsClient?.muted) {
      this._ttsQueue = []
      this._ttsQueueBusy = false
      this.isPlaying = false
      this.updateStopBtn()
      return
    }
    const next = this._ttsQueue?.shift()
    if (!next) return
    this._ttsQueueBusy = true

    if (next.emotion) {
      this.triggerFacialEmotion(next.emotion, next.intensity)
      this.showEmotion(next.emotion)
    }

    const data = await next.promise
    if (!data) {
      // Prefetch failed — skip this chunk, drain next
      this._ttsQueueBusy = false
      this._drainTtsQueue()
      return
    }

    const vrm = this.getVRM()

    if (vrm?.expressionManager) {
      try {
        if (!this._ttsClient._audioCtx) this._ttsClient._audioCtx = sharedAudioCtx || warmAudioContext()
        if (this._ttsClient._audioCtx.state === 'suspended') await this._ttsClient._audioCtx.resume()
        if (!this._ttsClient._gainNode) {
          this._ttsClient._gainNode = this._ttsClient._audioCtx.createGain()
          this._ttsClient._gainNode.connect(this._ttsClient._audioCtx.destination)
        }
        this._ttsClient._gainNode.gain.value = this._ttsClient._volume
        const skipAnim = this._ttsChunksPlayed > 0
        this._ttsChunksPlayed = (this._ttsChunksPlayed || 0) + 1
        await this._ttsClient._playWithLipSync(data, vrm, { skipAnimationStart: skipAnim })
      } catch (err) {
        console.error('[TTS] prefetch playback failed', err)
        // Playback failed — _onSpeechDone won't fire, so reset manually
        this._ttsQueueBusy = false
        this._drainTtsQueue()
      }
    } else {
      this._ttsClient.speak(next.text, vrm)
    }
    // _onSpeechDone handles resetting _ttsQueueBusy and draining next
  }

  flushAndCleanup() {
    this.innerObserver?.disconnect()
    this.innerObserver = null
    this.lastObservedLength = 0

    const full = this.streamingContentEl?.textContent || this.fullStreamedText || ''
    this.streamingContentEl = null

    // Keep the facial expression update — inner observer mirrored it during
    // streaming, but TTS enqueue is now the outer observer's job (driven by
    // the finalized message partial's data-tts-pending flag). Queueing the
    // "tail" text here was causing every bridge reply to play twice: once
    // from this tail enqueue, and again when the tts-pending handler ran.
    const { emotion, intensity } = processMessage(full, { allowedTags: this._availableEmotions })
    if (emotion) {
      this.triggerFacialEmotion(emotion, intensity)
      this.showEmotion(emotion)
    }

    this.fullStreamedText = ''
    this.sentenceBuffer = ''
    this._spokenLength = 0
    this.stripTagsFromStreaming()
  }

  stripTagsFromStreaming() {
    const streamingContent = document.getElementById('streaming-content')
    if (!streamingContent) return
    streamingContent.innerHTML = streamingContent.innerHTML
      .replace(/\[spoken\][\s\S]*?\[\/spoken\]/gi, '')
      .replace(/\[[^\]]+\]\s*/g, '')
  }

  // --- TTS playback ---

  async _speakText(text, messageId) {
    this._lastSpokenText = text
    this._lastMessageId = messageId

    if (this.isPlaying) this.cancelSpeech()

    this.isPlaying = true
    this.updateStopBtn()
    document.dispatchEvent(new CustomEvent("tts:start"))

    const vrm = this.getVRM()
    this._ttsClient.speak(text, vrm, { messageId })
  }

  getVRM() {
    const avatarEl = document.querySelector('[data-controller="avatar"]')
    return avatarEl?.vrmInstance || null
  }

  getAvatarController() {
    const avatarEl = document.querySelector('[data-controller="avatar"]')
    return avatarEl?.avatarController || null
  }

  triggerBodyAnimation(name) {
    const controller = this.getAvatarController()
    if (!controller) return
    // System roles are prefixed with __ in the animation registry to avoid
    // name collisions with custom animations (e.g. an animation named "idle")
    const SYSTEM_STATES = ['idle', 'talking', 'thinking']
    const key = SYSTEM_STATES.includes(name) ? `__${name}` : name
    controller.playAnimation(key)
  }

  triggerFacialEmotion(emotion, intensity = 1.0) {
    const controller = this.getAvatarController()
    if (controller) {
      controller.setFacialEmotion(emotion, intensity)
    }
  }

  cancelSpeech() {
    const wasPlaying = this.isPlaying
    this.sentenceBuffer = ''
    this._ttsChunksPlayed = 0
    this._ttsQueue = []
    this._ttsQueueBusy = false
    this._ttsClient.cancel()
    this.isPlaying = false
    this.updateStopBtn()
    if (wasPlaying) {
      document.dispatchEvent(new CustomEvent("tts:end"))
    }
  }
}
